/**
 * Calculs partagés par l'historique et les rapports : périodes, filtres, agrégats, export CSV.
 * Source : historique CBS du caissier (une date par opération, sans heure).
 */
import type { HistoryTransaction } from '../../hooks/useOperationHistory';

export type TypeFiltre = 'tous' | 'versement' | 'retrait';

export interface Periode {
  du: string; // AAAA-MM-JJ (inclus)
  au: string; // AAAA-MM-JJ (inclus)
}

export type PeriodeRapide = 'jour' | 'hier' | 'semaine' | 'mois' | 'mois-precedent' | '30j' | 'tout';

// ─── Dates (heure locale) ───────────────────────────────────────────────────

export function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function depuisIso(s: string): Date {
  const [a, m, j] = s.split('-').map(Number);
  return new Date(a, (m || 1) - 1, j || 1);
}

export function periodeRapide(code: PeriodeRapide, maintenant = new Date()): Periode {
  const auj = new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate());
  const decale = (jours: number) => new Date(auj.getFullYear(), auj.getMonth(), auj.getDate() + jours);
  switch (code) {
    case 'jour':
      return { du: iso(auj), au: iso(auj) };
    case 'hier':
      return { du: iso(decale(-1)), au: iso(decale(-1)) };
    case 'semaine': {
      const lundi = decale(-((auj.getDay() + 6) % 7));
      return { du: iso(lundi), au: iso(auj) };
    }
    case 'mois':
      return { du: iso(new Date(auj.getFullYear(), auj.getMonth(), 1)), au: iso(auj) };
    case 'mois-precedent':
      return {
        du: iso(new Date(auj.getFullYear(), auj.getMonth() - 1, 1)),
        au: iso(new Date(auj.getFullYear(), auj.getMonth(), 0)),
      };
    case '30j':
      return { du: iso(decale(-29)), au: iso(auj) };
    case 'tout':
    default:
      return { du: '', au: '' };
  }
}

const FORMAT_JOUR = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const FORMAT_COURT = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

export const libelleJour = (isoJour: string) => {
  const t = FORMAT_JOUR.format(depuisIso(isoJour));
  return t.charAt(0).toUpperCase() + t.slice(1);
};
export const dateCourte = (d: Date | string) => FORMAT_COURT.format(typeof d === 'string' ? depuisIso(d) : d);

export function libellePeriode(p: Periode): string {
  if (!p.du && !p.au) return "Toute l'activité";
  if (p.du && p.au && p.du === p.au) return `Journée du ${dateCourte(p.du)}`;
  if (p.du && p.au) return `Du ${dateCourte(p.du)} au ${dateCourte(p.au)}`;
  return p.du ? `Depuis le ${dateCourte(p.du)}` : `Jusqu'au ${dateCourte(p.au)}`;
}

// ─── Montants ───────────────────────────────────────────────────────────────

const NOMBRE = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
export const fcfa = (n: number) => `${NOMBRE.format(Math.round(n))} FCFA`;
export const nombre = (n: number) => NOMBRE.format(n);

// ─── Filtrage ───────────────────────────────────────────────────────────────

export function filtrer(
  ops: HistoryTransaction[],
  { periode, type = 'tous', recherche = '' }: { periode: Periode; type?: TypeFiltre; recherche?: string },
): HistoryTransaction[] {
  const q = recherche.trim().toLowerCase();
  return ops.filter((t) => {
    const jour = iso(t.date);
    if (periode.du && jour < periode.du) return false;
    if (periode.au && jour > periode.au) return false;
    if (type !== 'tous' && t.type !== type) return false;
    if (!q) return true;
    return [t.accountNumber, t.reference, t.motif, t.clientName, t.deposantNom, t.cashier]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(q));
  });
}

// ─── Agrégats ───────────────────────────────────────────────────────────────

export interface Totaux {
  nbVersements: number;
  montantVersements: number;
  nbRetraits: number;
  montantRetraits: number;
  net: number;
  nbOperations: number;
  moyenneVersement: number;
  moyenneRetrait: number;
  plusGrosse: HistoryTransaction | null;
}

export function totaux(ops: HistoryTransaction[]): Totaux {
  let nbV = 0, mV = 0, nbR = 0, mR = 0;
  let plusGrosse: HistoryTransaction | null = null;
  for (const t of ops) {
    if (t.type === 'versement') { nbV++; mV += t.amount; } else { nbR++; mR += t.amount; }
    if (!plusGrosse || t.amount > plusGrosse.amount) plusGrosse = t;
  }
  return {
    nbVersements: nbV,
    montantVersements: mV,
    nbRetraits: nbR,
    montantRetraits: mR,
    net: mV - mR,
    nbOperations: ops.length,
    moyenneVersement: nbV ? mV / nbV : 0,
    moyenneRetrait: nbR ? mR / nbR : 0,
    plusGrosse,
  };
}

export interface LigneJour {
  jour: string; // AAAA-MM-JJ
  nbVersements: number;
  montantVersements: number;
  nbRetraits: number;
  montantRetraits: number;
  net: number;
}

/** Récapitulatif par jour, du plus récent au plus ancien */
export function parJour(ops: HistoryTransaction[]): LigneJour[] {
  const map = new Map<string, LigneJour>();
  for (const t of ops) {
    const jour = iso(t.date);
    const l = map.get(jour) ?? { jour, nbVersements: 0, montantVersements: 0, nbRetraits: 0, montantRetraits: 0, net: 0 };
    if (t.type === 'versement') { l.nbVersements++; l.montantVersements += t.amount; } else { l.nbRetraits++; l.montantRetraits += t.amount; }
    l.net = l.montantVersements - l.montantRetraits;
    map.set(jour, l);
  }
  return [...map.values()].sort((a, b) => (a.jour < b.jour ? 1 : -1));
}

export interface LigneClient {
  compte: string;
  nom: string;
  nbOperations: number;
  verse: number;
  retire: number;
  volume: number;
}

export function principauxClients(ops: HistoryTransaction[], limite = 5): LigneClient[] {
  const map = new Map<string, LigneClient>();
  for (const t of ops) {
    const cle = t.accountNumber || t.clientName || '—';
    const l = map.get(cle) ?? { compte: t.accountNumber, nom: t.clientName || 'Client', nbOperations: 0, verse: 0, retire: 0, volume: 0 };
    l.nbOperations++;
    if (t.type === 'versement') l.verse += t.amount; else l.retire += t.amount;
    l.volume += t.amount;
    map.set(cle, l);
  }
  return [...map.values()].sort((a, b) => b.volume - a.volume).slice(0, limite);
}

export function parMotif(ops: HistoryTransaction[], type: 'versement' | 'retrait', limite = 6) {
  const map = new Map<string, { motif: string; nb: number; montant: number }>();
  for (const t of ops) {
    if (t.type !== type) continue;
    const motif = (t.motif || 'Non précisé').trim();
    const l = map.get(motif) ?? { motif, nb: 0, montant: 0 };
    l.nb++;
    l.montant += t.amount;
    map.set(motif, l);
  }
  return [...map.values()].sort((a, b) => b.montant - a.montant).slice(0, limite);
}

// ─── Export CSV (compatible Excel : « ; », BOM UTF-8, formules neutralisées) ─

function cellule(v: unknown): string {
  let s = v == null ? '' : String(v).replace(/\r?\n/g, ' ');
  if (/^[=+\-@]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

export function telechargerCsv(nomFichier: string, entetes: string[], lignes: unknown[][]) {
  const contenu = '﻿' + [entetes, ...lignes].map((l) => l.map(cellule).join(';')).join('\r\n');
  const url = URL.createObjectURL(new Blob([contenu], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nomFichier;
  a.click();
  URL.revokeObjectURL(url);
}

export function exporterOperationsCsv(ops: HistoryTransaction[], nomFichier: string) {
  telechargerCsv(
    nomFichier,
    ['Date', 'Type', 'Référence', 'Compte', 'Client', 'Motif', 'Montant (FCFA)', 'Sens', 'Caissier'],
    ops.map((t) => [
      dateCourte(t.date),
      t.type === 'versement' ? 'Versement' : 'Retrait',
      t.reference,
      t.accountNumber,
      t.clientName,
      t.motif,
      t.amount,
      t.type === 'versement' ? 'Crédit' : 'Débit',
      t.cashier,
    ]),
  );
}

/** Impression d'un document HTML via une iframe masquée (pas de fenêtre pop-up) */
export function imprimerHtml(html: string) {
  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) {
    iframe.remove();
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();
  const lancer = () => {
    win.focus();
    win.print();
    setTimeout(() => iframe.remove(), 1000);
  };
  const images = Array.from(doc.images).filter((i) => !i.complete);
  if (images.length === 0) setTimeout(lancer, 50);
  else {
    let restantes = images.length;
    images.forEach((i) => {
      const fin = () => { if (--restantes === 0) lancer(); };
      i.addEventListener('load', fin, { once: true });
      i.addEventListener('error', fin, { once: true });
    });
  }
}

export const echapperHtml = (v: unknown) =>
  String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
